import { useState, useMemo } from 'react'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/shared/ui/collapsible'
import { ChevronRightIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { t } from '@/shared/lib/i18n'
import type {
  SiteOption,
  DepartmentOption,
  UnitOption,
  TreeData,
  TreeSite,
  TreeDepartment,
  CheckState,
  MaterializedIds,
} from './useAccessTree'

interface MatchesSearch {
  matchingUnitIds: Set<number>
  matchingDepartmentIds: Set<number>
  matchingSiteIds: Set<number>
}

interface AccessTreeProps {
  sites: SiteOption[]
  departments: DepartmentOption[]
  units: UnitOption[]
  tree: TreeData
  selectedUnitIds: Set<number>
  toggleUnit: (unitId: number) => void
  toggleDepartment: (department: TreeDepartment) => void
  toggleSite: (site: TreeSite) => void
  toggleAll: () => void
  selectAllState: CheckState
  computeDepartmentState: (department: TreeDepartment) => CheckState
  computeSiteState: (site: TreeSite) => CheckState
  materialized: MaterializedIds
  readOnly?: boolean
}

export const AccessTree = ({
  sites,
  departments,
  units,
  tree,
  selectedUnitIds,
  toggleUnit,
  toggleDepartment,
  toggleSite,
  toggleAll,
  selectAllState,
  computeDepartmentState,
  computeSiteState,
  materialized,
  readOnly = false,
}: AccessTreeProps) => {
  const [search, setSearch] = useState('')
  const [userExpandedSites, setUserExpandedSites] = useState<Set<number>>(() => new Set())
  const [userExpandedDepartments, setUserExpandedDepartments] = useState<Set<number>>(
    () => new Set(),
  )

  const searchLower = search.toLowerCase()

  const matchesSearch = useMemo<MatchesSearch | null>(() => {
    if (!searchLower) return null
    const matchingUnitIds = new Set<number>()
    const matchingDepartmentIds = new Set<number>()
    const matchingSiteIds = new Set<number>()

    for (const unit of units) {
      if (unit.name.toLowerCase().includes(searchLower)) matchingUnitIds.add(unit.id)
    }
    for (const department of departments) {
      if (department.name.toLowerCase().includes(searchLower))
        matchingDepartmentIds.add(department.id)
    }
    for (const site of sites) {
      if (site.name.toLowerCase().includes(searchLower)) matchingSiteIds.add(site.id)
    }
    return { matchingUnitIds, matchingDepartmentIds, matchingSiteIds }
  }, [searchLower, units, departments, sites])

  // Derived: which nodes must be expanded to reveal search matches.
  // Computed inline — no setState in effect needed, no cascading renders.
  const searchDrivenExpansions = useMemo(() => {
    if (!matchesSearch) return null

    const sitesToExpand = new Set<number>()
    const departmentsToExpand = new Set<number>()

    for (const site of tree.sites) {
      if (matchesSearch.matchingSiteIds.has(site.id)) sitesToExpand.add(site.id)
      for (const department of site.departments) {
        if (matchesSearch.matchingDepartmentIds.has(department.id)) {
          sitesToExpand.add(site.id)
          departmentsToExpand.add(department.id)
        }
        for (const unit of department.units) {
          if (matchesSearch.matchingUnitIds.has(unit.id)) {
            sitesToExpand.add(site.id)
            departmentsToExpand.add(department.id)
          }
        }
      }
      for (const unit of site.unassignedUnits) {
        if (matchesSearch.matchingUnitIds.has(unit.id)) sitesToExpand.add(site.id)
      }
    }

    for (const department of tree.unassignedDepartments) {
      if (matchesSearch.matchingDepartmentIds.has(department.id)) {
        departmentsToExpand.add(department.id)
      }
      for (const unit of department.units) {
        if (matchesSearch.matchingUnitIds.has(unit.id)) departmentsToExpand.add(department.id)
      }
    }

    return { sitesToExpand, departmentsToExpand }
  }, [matchesSearch, tree])

  // Merge user-controlled expand state with search-driven expansions so both work
  // simultaneously. User expansions survive search and are restored when cleared.
  const expandedSites = useMemo(
    () =>
      searchDrivenExpansions
        ? new Set([...userExpandedSites, ...searchDrivenExpansions.sitesToExpand])
        : userExpandedSites,
    [userExpandedSites, searchDrivenExpansions],
  )

  const expandedDepartments = useMemo(
    () =>
      searchDrivenExpansions
        ? new Set([...userExpandedDepartments, ...searchDrivenExpansions.departmentsToExpand])
        : userExpandedDepartments,
    [userExpandedDepartments, searchDrivenExpansions],
  )

  const expandAll = () => {
    setUserExpandedSites(new Set(tree.sites.map((site) => site.id)))
    setUserExpandedDepartments(
      new Set([
        ...tree.sites.flatMap((site) => site.departments.map((dept) => dept.id)),
        ...tree.unassignedDepartments.map((dept) => dept.id),
      ]),
    )
  }

  const collapseAll = () => {
    setUserExpandedSites(new Set())
    setUserExpandedDepartments(new Set())
  }

  const toggleSiteExpanded = (siteId: number) => {
    setUserExpandedSites((previous) => {
      const next = new Set(previous)
      if (next.has(siteId)) next.delete(siteId)
      else next.add(siteId)
      return next
    })
  }

  const toggleDepartmentExpanded = (departmentId: number) => {
    setUserExpandedDepartments((previous) => {
      const next = new Set(previous)
      if (next.has(departmentId)) next.delete(departmentId)
      else next.add(departmentId)
      return next
    })
  }

  const selectedSitesCount = materialized.siteIds.length
  const selectedDepartmentsCount = materialized.departmentIds.length
  const selectedUnitsCount = materialized.unitIds.length

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {t('admin.users.access.selectedSummary', {
            sites: selectedSitesCount,
            departments: selectedDepartmentsCount,
            units: selectedUnitsCount,
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={expandAll}>
            {t('admin.users.access.expandAll')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={collapseAll}>
            {t('admin.users.access.collapseAll')}
          </Button>
        </div>
      </div>

      <Input
        placeholder={t('admin.users.access.searchPlaceholder')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <div className="flex items-center gap-3 border-b pb-3">
        <Checkbox
          id="access-tree-select-all"
          checked={selectAllState}
          onCheckedChange={readOnly ? undefined : toggleAll}
          disabled={readOnly}
        />
        <Label htmlFor="access-tree-select-all" className="cursor-pointer font-normal">
          {selectAllState ? t('admin.users.access.deselectAll') : t('admin.users.access.selectAll')}
        </Label>
      </div>

      <div className="space-y-1">
        {tree.sites.map((site) => (
          <SiteNode
            key={site.id}
            site={site}
            expanded={expandedSites.has(site.id)}
            onToggleExpand={() => toggleSiteExpanded(site.id)}
            checkState={computeSiteState(site)}
            onToggleCheck={() => toggleSite(site)}
            selectedUnitIds={selectedUnitIds}
            expandedDepartments={expandedDepartments}
            onToggleDepartmentExpand={toggleDepartmentExpanded}
            computeDepartmentState={computeDepartmentState}
            onToggleDepartment={toggleDepartment}
            onToggleUnit={toggleUnit}
            matchesSearch={matchesSearch}
            readOnly={readOnly}
          />
        ))}

        {tree.unassignedDepartments.length > 0 && (
          <div className="space-y-1 pt-2 border-t mt-2">
            <p className="text-xs text-muted-foreground font-medium pl-6 pb-1">
              {t('admin.users.access.unassignedDepartments')}
            </p>
            {tree.unassignedDepartments.map((department) => (
              <DepartmentNode
                key={department.id}
                department={department}
                expanded={expandedDepartments.has(department.id)}
                onToggleExpand={() => toggleDepartmentExpanded(department.id)}
                checkState={computeDepartmentState(department)}
                onToggleCheck={() => toggleDepartment(department)}
                selectedUnitIds={selectedUnitIds}
                onToggleUnit={toggleUnit}
                matchesSearch={matchesSearch}
                indentLevel={0}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}

        {tree.unassignedUnits.length > 0 && (
          <div className="space-y-1 pt-2 border-t mt-2">
            <p className="text-xs text-muted-foreground font-medium pl-6 pb-1">
              {t('admin.users.access.unassignedUnits')}
            </p>
            {tree.unassignedUnits.map((unit) => (
              <UnitNode
                key={unit.id}
                unit={unit}
                selected={selectedUnitIds.has(unit.id)}
                onToggle={() => toggleUnit(unit.id)}
                matchesSearch={matchesSearch}
                indentLevel={0}
                readOnly={readOnly}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Node components ────────────────────────────────────────────────────────────

interface SiteNodeProps {
  site: TreeSite
  expanded: boolean
  onToggleExpand: () => void
  checkState: CheckState
  onToggleCheck: () => void
  selectedUnitIds: Set<number>
  expandedDepartments: Set<number>
  onToggleDepartmentExpand: (departmentId: number) => void
  computeDepartmentState: (department: TreeDepartment) => CheckState
  onToggleDepartment: (department: TreeDepartment) => void
  onToggleUnit: (unitId: number) => void
  matchesSearch: MatchesSearch | null
  readOnly: boolean
}

const SiteNode = ({
  site,
  expanded,
  onToggleExpand,
  checkState,
  onToggleCheck,
  selectedUnitIds,
  expandedDepartments,
  onToggleDepartmentExpand,
  computeDepartmentState,
  onToggleDepartment,
  onToggleUnit,
  matchesSearch,
  readOnly,
}: SiteNodeProps) => {
  const hasChildren = site.departments.length > 0 || site.unassignedUnits.length > 0
  const isHighlighted = matchesSearch?.matchingSiteIds.has(site.id)

  return (
    <Collapsible open={expanded} onOpenChange={onToggleExpand}>
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex size-5 items-center justify-center rounded-sm transition-transform',
              !hasChildren && 'invisible',
            )}
          >
            <ChevronRightIcon
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                expanded && 'rotate-90',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <Checkbox
          id={`site-${site.id}`}
          checked={checkState}
          onCheckedChange={readOnly ? undefined : onToggleCheck}
          disabled={readOnly}
        />
        <Label
          htmlFor={`site-${site.id}`}
          className={cn(
            'cursor-pointer font-medium',
            isHighlighted && 'bg-sky-100 dark:bg-sky-800/30 px-1 rounded',
          )}
        >
          {site.name}
        </Label>
      </div>
      <CollapsibleContent>
        <div className="ml-5 border-l pl-2 space-y-0.5">
          {site.departments.map((department) => (
            <DepartmentNode
              key={department.id}
              department={department}
              expanded={expandedDepartments.has(department.id)}
              onToggleExpand={() => onToggleDepartmentExpand(department.id)}
              checkState={computeDepartmentState(department)}
              onToggleCheck={() => onToggleDepartment(department)}
              selectedUnitIds={selectedUnitIds}
              onToggleUnit={onToggleUnit}
              matchesSearch={matchesSearch}
              indentLevel={1}
              readOnly={readOnly}
            />
          ))}
          {site.unassignedUnits.map((unit) => (
            <UnitNode
              key={unit.id}
              unit={unit}
              selected={selectedUnitIds.has(unit.id)}
              onToggle={() => onToggleUnit(unit.id)}
              matchesSearch={matchesSearch}
              indentLevel={1}
              readOnly={readOnly}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

interface DepartmentNodeProps {
  department: TreeDepartment
  expanded: boolean
  onToggleExpand: () => void
  checkState: CheckState
  onToggleCheck: () => void
  selectedUnitIds: Set<number>
  onToggleUnit: (unitId: number) => void
  matchesSearch: MatchesSearch | null
  indentLevel: number
  readOnly: boolean
}

const DepartmentNode = ({
  department,
  expanded,
  onToggleExpand,
  checkState,
  onToggleCheck,
  selectedUnitIds,
  onToggleUnit,
  matchesSearch,
  indentLevel: _indentLevel,
  readOnly,
}: DepartmentNodeProps) => {
  const hasChildren = department.units.length > 0
  const isHighlighted = matchesSearch?.matchingDepartmentIds.has(department.id)

  return (
    <Collapsible open={expanded} onOpenChange={onToggleExpand}>
      <div className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex size-5 items-center justify-center rounded-sm transition-transform',
              !hasChildren && 'invisible',
            )}
          >
            <ChevronRightIcon
              className={cn(
                'size-3.5 text-muted-foreground transition-transform',
                expanded && 'rotate-90',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <Checkbox
          id={`dept-${department.id}`}
          checked={checkState}
          onCheckedChange={readOnly ? undefined : onToggleCheck}
          disabled={readOnly}
        />
        <Label
          htmlFor={`dept-${department.id}`}
          className={cn(
            'cursor-pointer text-sm',
            isHighlighted && 'bg-sky-100 dark:bg-sky-800/30 px-1 rounded',
          )}
        >
          {department.name}
        </Label>
      </div>
      {hasChildren && (
        <CollapsibleContent>
          <div className="ml-5 border-l pl-2 space-y-0.5">
            {department.units.map((unit) => (
              <UnitNode
                key={unit.id}
                unit={unit}
                selected={selectedUnitIds.has(unit.id)}
                onToggle={() => onToggleUnit(unit.id)}
                matchesSearch={matchesSearch}
                indentLevel={_indentLevel + 1}
                readOnly={readOnly}
              />
            ))}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  )
}

interface UnitNodeProps {
  unit: UnitOption
  selected: boolean
  onToggle: () => void
  matchesSearch: MatchesSearch | null
  indentLevel: number
  readOnly: boolean
}

const UnitNode = ({
  unit,
  selected,
  onToggle,
  matchesSearch,
  indentLevel: _indentLevel,
  readOnly,
}: UnitNodeProps) => {
  const isHighlighted = matchesSearch?.matchingUnitIds.has(unit.id)
  const isHidden = matchesSearch && !matchesSearch.matchingUnitIds.has(unit.id)

  if (isHidden) return null

  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50">
      <div className="size-5" />
      <Checkbox
        id={`unit-${unit.id}`}
        checked={selected}
        onCheckedChange={readOnly ? undefined : onToggle}
        disabled={readOnly}
      />
      <Label
        htmlFor={`unit-${unit.id}`}
        className={cn(
          'cursor-pointer text-sm font-normal',
          isHighlighted && 'bg-sky-100 dark:bg-sky-800/30 px-1 rounded',
        )}
      >
        {unit.name}
      </Label>
    </div>
  )
}
